import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Button } from "@progress/kendo-react-buttons";
import { Input, TextArea, NumericTextBox, Checkbox } from "@progress/kendo-react-inputs";
import { DatePickerWithYearSanitizer } from "@amsanalytics/react-shared-library";
import { useState, useEffect } from "react";
import { ICreateUpdateCustomerVehicleInsurance, defaultCreateUpdateCustomerVehicleInsurance } from "../../../Interfaces/Customer/IAppCustomerVehicleInsurance";
import { AmsInsuranceAgent } from "../../../Interfaces/Insurance/AmsInsuranceAgent";
import { InsuranceAgentSelector } from "./InsuranceAgentSelector";
import styles from "./_insurance-form.module.scss";

export interface InsuranceFormWindowProps {
    visible: boolean;
    mode: "create" | "edit";
    initialData?: ICreateUpdateCustomerVehicleInsurance | null;
    existingInsurance?: ICreateUpdateCustomerVehicleInsurance[];
    insuranceAgents: AmsInsuranceAgent[];
    onSave: (data: ICreateUpdateCustomerVehicleInsurance) => void;
    onCancel: () => void;
    appCustomerId: string;
    vehicleStockNumber?: string;
    warningMessage?: string;
    defaultCpiFactor?: number;
    defaultCpiNextAccrueDate?: Date | null;
    loanPayFrequencyLabel?: string;
}

export function InsuranceFormWindow({
    visible,
    mode,
    initialData,
    insuranceAgents,
    onSave,
    onCancel,
    appCustomerId,
    vehicleStockNumber = '',
    warningMessage,
    defaultCpiFactor,
    defaultCpiNextAccrueDate,
    loanPayFrequencyLabel,
}: InsuranceFormWindowProps) {
    const [formData, setFormData] = useState<ICreateUpdateCustomerVehicleInsurance>({
        ...defaultCreateUpdateCustomerVehicleInsurance,
        appCustomerId,
    });

    const [errors, setErrors] = useState<Record<string, string>>({});
    const [showCpiFields, setShowCpiFields] = useState<boolean>(false);
    const [showAgentSelector, setShowAgentSelector] = useState<boolean>(false);

    const isFormValid = (): boolean => {
        if (showCpiFields) {
            const hasCpiErrors =
                (formData.cpiSemi1DayOfMonth != null && (formData.cpiSemi1DayOfMonth < 1 || formData.cpiSemi1DayOfMonth > 31)) ||
                (formData.cpiSemi2DayOfMonth != null && (formData.cpiSemi2DayOfMonth < 1 || formData.cpiSemi2DayOfMonth > 31));
            return !hasCpiErrors;
        }

        const hasRequiredFields =
            !!formData.insuranceCompany?.trim() &&
            !!formData.policyNumber?.trim() &&
            !!formData.effectiveDate &&
            !!formData.expirationDate;

        const hasNoErrors = Object.keys(errors).length === 0;

        return hasRequiredFields && hasNoErrors;
    };

    const [prevVisible, setPrevVisible] = useState(false);

    useEffect(() => {
        // Only initialize form when dialog first opens (visible transitions from false to true)
        if (visible && !prevVisible) {
            if (initialData) {
                setFormData({ ...initialData, appCustomerId, stockNumber: initialData.stockNumber || vehicleStockNumber });
                setShowCpiFields(initialData.isCpi ?? false);
            } else if (mode === "create") {
                setFormData({
                    ...defaultCreateUpdateCustomerVehicleInsurance,
                    appCustomerId,
                    stockNumber: vehicleStockNumber,
                });
                setShowCpiFields(false);
            }
            setErrors({});
        }
        setPrevVisible(visible);
    }, [visible, initialData, mode, appCustomerId]);

    // Sync CPI factor when the async fetch completes after the checkbox was already toggled
    useEffect(() => {
        if (showCpiFields && mode === "create" && defaultCpiFactor && (formData.cpiFactor ?? 0) === 0) {
            setFormData(prev => ({ ...prev, cpiFactor: defaultCpiFactor }));
        }
    }, [defaultCpiFactor, showCpiFields, mode]);

    const validateForm = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (showCpiFields) {
            if (formData.cpiSemi1DayOfMonth != null && (formData.cpiSemi1DayOfMonth < 1 || formData.cpiSemi1DayOfMonth > 31)) {
                newErrors.cpiSemi1DayOfMonth = "Day of month must be between 1 and 31";
            }
            if (formData.cpiSemi2DayOfMonth != null && (formData.cpiSemi2DayOfMonth < 1 || formData.cpiSemi2DayOfMonth > 31)) {
                newErrors.cpiSemi2DayOfMonth = "Day of month must be between 1 and 31";
            }
            setErrors(newErrors);
            return Object.keys(newErrors).length === 0;
        }

        if (!formData.insuranceCompany?.trim()) {
            newErrors.insuranceCompany = "Insurance company is required";
        }
        if (!formData.policyNumber?.trim()) {
            newErrors.policyNumber = "Policy number is required";
        }
        if (!formData.effectiveDate) {
            newErrors.effectiveDate = "Effective date is required";
        }
        if (!formData.expirationDate) {
            newErrors.expirationDate = "Expiration date is required";
        }

        if (formData.effectiveDate && formData.expirationDate) {
            const effective = new Date(formData.effectiveDate);
            const expiration = new Date(formData.expirationDate);
            if (expiration < effective) {
                newErrors.expirationDate = "Expiration date cannot be before effective date";
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = () => {
        if (validateForm()) {
            let saveData = { ...formData, isCpi: showCpiFields };
            if (showCpiFields) {
                const today = new Date();
                const oneYearFromNow = new Date();
                oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);
                saveData = {
                    ...saveData,
                    insuranceCompany: "CPI",
                    policyNumber: "CPI",
                    effectiveDate: saveData.effectiveDate || today,
                    expirationDate: saveData.expirationDate || oneYearFromNow,
                };
            }
            onSave(saveData);
        }
    };

    const handleAgentSelected = (agent: AmsInsuranceAgent) => {
        setFormData((prev) => ({
            ...prev,
            insuranceCompany: agent.compName || "",
            insuranceCompanyPhone: agent.compPhone || "",
            agent: agent.agentName || "",
            agentPhone: agent.agentPhone || "",
            fax: agent.fax || "",
            collisionDeductible: agent.collDeduct || 0,
            comprehensiveDeductible: agent.compDeduct || 0,
        }));

        setErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors.insuranceCompany;
            return newErrors;
        });

        setShowAgentSelector(false);
    };

    const toDate = (val: any): Date | null => {
        if (!val) return null;
        if (val instanceof Date) return val;
        const d = new Date(val);
        return isNaN(d.getTime()) ? null : d;
    };

    if (!visible) return null;

    return (
        <Dialog
            title={mode === "create" ? "Add Insurance" : "Edit Insurance"}
            onClose={onCancel}
            width={showCpiFields ? "min(600px, 95vw)" : "min(800px, 95vw)"}
            minWidth={300}
            height={showCpiFields ? "auto" : warningMessage ? "min(1020px, 95vh)" : "min(960px, 95vh)"}
        >
            <div className={styles.formGrid}>
                {warningMessage && (
                    <div className={`${styles.fieldGroup} ${styles.fullWidth}`}
                        style={{
                            backgroundColor: '#fff3cd',
                            border: '1px solid #ffc107',
                            borderRadius: '4px',
                            padding: '10px 15px',
                            color: '#856404',
                            marginBottom: '0.5rem',
                        }}
                    >
                        <span style={{ fontWeight: 600 }}>{warningMessage}</span>
                    </div>
                )}
                {!showCpiFields && (
                    <div className={`${styles.fieldGroup} ${styles.fullWidth}`}>
                        <Button
                            fillMode="outline"
                            themeColor="primary"
                            onClick={() => setShowAgentSelector(true)}
                            style={{ marginBottom: "1rem" }}
                        >
                            Select Insurance/Agent
                        </Button>
                    </div>
                )}

                <div className={styles.fieldGroup}>
                    <Checkbox
                        label="CPI"
                        value={showCpiFields}
                        onChange={(e) => {
                            const isCpi = e.value ?? false;
                            setShowCpiFields(isCpi);
                            if (isCpi) {
                                const isPlacing = mode === "create" && !initialData?.isCpi;
                                setFormData((prev) => ({
                                    ...prev,
                                    insuranceCompany: "CPI",
                                    policyNumber: "CPI",
                                    ...(isPlacing ? {
                                        cpiFactor: defaultCpiFactor ?? 0,
                                        cpiStartDate: new Date(),
                                        cpiNextAccrueDate: defaultCpiNextAccrueDate ?? null,
                                        cpiSemi1DayOfMonth: null,
                                        cpiSemi2DayOfMonth: null,
                                    } : {}),
                                }));
                                setErrors({});
                            }
                        }}
                    />
                </div>

                {/* CPI fields */}
                {showCpiFields && (
                    <>
                        {/* empty spacer so nothing appears to the right of the CPI checkbox */}
                        <div />
                        {loanPayFrequencyLabel ? (
                            <div className={styles.fieldGroup}
                                style={{
                                    backgroundColor: '#e8f4fd',
                                    border: '1px solid #b3d9f2',
                                    borderRadius: '4px',
                                    padding: '8px 15px',
                                    color: '#1a5276',
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                            >
                                <span><strong>Pay Frequency:</strong> {loanPayFrequencyLabel}</span>
                            </div>
                        ) : <div />}
                        <div className={styles.fieldGroup}>
                            <label>CPI Factor</label>
                            <NumericTextBox
                                value={formData.cpiFactor ?? 0}
                                onChange={(e) => setFormData((prev) => ({ ...prev, cpiFactor: e.value ?? 0 }))}
                                format="c2"
                                min={0}
                                placeholder="Premium Amount"
                            />
                        </div>
                        <div className={styles.fieldGroup}>
                            <label>CPI Start Date</label>
                            <DatePickerWithYearSanitizer
                                value={toDate(formData.cpiStartDate)}
                                onChange={(e) => setFormData((prev) => ({ ...prev, cpiStartDate: e.value }))}
                                format="MM/dd/yyyy"
                            />
                        </div>

                        <div className={styles.fieldGroup}>
                            <label>CPI Next Accrue Date</label>
                            <DatePickerWithYearSanitizer
                                value={toDate(formData.cpiNextAccrueDate)}
                                onChange={(e) => setFormData((prev) => ({ ...prev, cpiNextAccrueDate: e.value }))}
                                format="MM/dd/yyyy"
                            />
                        </div>
                        <div className={styles.fieldGroup}>
                            <label>Semi 1 Day of Month</label>
                            <NumericTextBox
                                value={formData.cpiSemi1DayOfMonth ?? null}
                                onChange={(e) => setFormData((prev) => ({ ...prev, cpiSemi1DayOfMonth: e.value }))}
                                min={1}
                                max={31}
                                format="n0"
                                placeholder="1-31"
                            />
                        </div>

                        <div className={styles.fieldGroup}>
                            <label>Semi 2 Day of Month</label>
                            <NumericTextBox
                                value={formData.cpiSemi2DayOfMonth ?? null}
                                onChange={(e) => setFormData((prev) => ({ ...prev, cpiSemi2DayOfMonth: e.value }))}
                                min={1}
                                max={31}
                                format="n0"
                                placeholder="1-31"
                            />
                        </div>
                    </>
                )}

                {!showCpiFields && (
                    <>
                        <div className={`${styles.fieldGroup} ${styles.fullWidth}`}>
                            <label className={styles.required}>Insurance Company</label>
                            <Input
                                value={formData.insuranceCompany}
                                onChange={(e) => {
                                    setFormData((prev) => ({ ...prev, insuranceCompany: e.value }));
                                    if (!e.value?.trim()) {
                                        setErrors((prev) => ({ ...prev, insuranceCompany: "Insurance company is required" }));
                                    } else {
                                        setErrors((prev) => { const n = { ...prev }; delete n.insuranceCompany; return n; });
                                    }
                                }}
                                validationMessage={errors.insuranceCompany}
                                valid={!errors.insuranceCompany}
                            />
                        </div>

                        <div className={styles.fieldGroup}>
                            <label className={styles.required}>Policy Number</label>
                            <Input
                                value={formData.policyNumber}
                                onChange={(e) => {
                                    setFormData((prev) => ({ ...prev, policyNumber: e.value }));
                                    if (!e.value?.trim()) {
                                        setErrors((prev) => ({ ...prev, policyNumber: "Policy number is required" }));
                                    } else {
                                        setErrors((prev) => { const n = { ...prev }; delete n.policyNumber; return n; });
                                    }
                                }}
                                validationMessage={errors.policyNumber}
                                valid={!errors.policyNumber}
                            />
                        </div>

                        <div className={styles.fieldGroup}>
                            <label>Stock Number</label>
                            <Input
                                value={formData.stockNumber}
                                disabled={true}
                            />
                        </div>

                        <div className={styles.fieldGroup}>
                            <label>Agent Name</label>
                            <Input
                                value={formData.agent}
                                onChange={(e) => setFormData((prev) => ({ ...prev, agent: e.value }))}
                            />
                        </div>

                        <div className={styles.fieldGroup}>
                            <label>Agent Phone</label>
                            <Input
                                value={formData.agentPhone}
                                onChange={(e) => setFormData((prev) => ({ ...prev, agentPhone: e.value }))}
                            />
                        </div>

                        <div className={styles.fieldGroup}>
                            <label>Company Phone</label>
                            <Input
                                value={formData.insuranceCompanyPhone}
                                onChange={(e) => setFormData((prev) => ({ ...prev, insuranceCompanyPhone: e.value }))}
                            />
                        </div>

                        <div className={styles.fieldGroup}>
                            <label>Fax</label>
                            <Input
                                value={formData.fax}
                                onChange={(e) => setFormData((prev) => ({ ...prev, fax: e.value }))}
                            />
                        </div>

                        <div className={styles.fieldGroup}>
                            <label className={styles.required}>Effective Date</label>
                            <DatePickerWithYearSanitizer
                                value={toDate(formData.effectiveDate)}
                                onChange={(e) => {
                                    setFormData((prev) => ({ ...prev, effectiveDate: e.value }));
                                    if (!e.value) {
                                        setErrors((prev) => ({ ...prev, effectiveDate: "Effective date is required" }));
                                    } else {
                                        setErrors((prev) => { const n = { ...prev }; delete n.effectiveDate; return n; });
                                    }
                                }}
                                format="MM/dd/yyyy"
                                validationMessage={errors.effectiveDate}
                                valid={!errors.effectiveDate}
                            />
                        </div>

                        <div className={styles.fieldGroup}>
                            <label className={styles.required}>Expiration Date</label>
                            <DatePickerWithYearSanitizer
                                value={toDate(formData.expirationDate)}
                                onChange={(e) => {
                                    setFormData((prev) => ({ ...prev, expirationDate: e.value }));
                                    if (!e.value) {
                                        setErrors((prev) => ({ ...prev, expirationDate: "Expiration date is required" }));
                                    } else {
                                        setErrors((prev) => { const n = { ...prev }; delete n.expirationDate; return n; });
                                    }
                                }}
                                format="MM/dd/yyyy"
                                min={toDate(formData.effectiveDate) ?? undefined}
                                validationMessage={errors.expirationDate}
                                valid={!errors.expirationDate}
                            />
                        </div>

                        <div className={styles.fieldGroup}>
                            <label>Cancellation Date</label>
                            <DatePickerWithYearSanitizer
                                value={toDate(formData.cancellationDate)}
                                onChange={(e) => setFormData((prev) => ({ ...prev, cancellationDate: e.value }))}
                                format="MM/dd/yyyy"
                            />
                        </div>

                        <div className={styles.fieldGroup}>
                            <label>Collision Deductible</label>
                            <NumericTextBox
                                value={formData.collisionDeductible}
                                onChange={(e) => setFormData((prev) => ({ ...prev, collisionDeductible: e.value ?? 0 }))}
                                format="c2"
                            />
                        </div>

                        <div className={styles.fieldGroup}>
                            <label>Comprehensive Deductible</label>
                            <NumericTextBox
                                value={formData.comprehensiveDeductible ?? 0}
                                onChange={(e) => setFormData((prev) => ({ ...prev, comprehensiveDeductible: e.value ?? 0 }))}
                                format="c2"
                            />
                        </div>
                    </>
                )}

                <div className={`${styles.fieldGroup} ${styles.fullWidth}`}>
                    <label>Memo</label>
                    <TextArea
                        value={formData.memo}
                        onChange={(e) => setFormData((prev) => ({ ...prev, memo: (e.value ?? "").slice(0, 50) }))}
                        rows={1}
                        maxLength={50}
                        style={{ resize: "none" }}
                    />
                    <span style={{ fontSize: "0.75rem", color: "#666", textAlign: "right" }}>{(formData.memo ?? "").length}/50</span>
                </div>

                <div className={`${styles.fieldGroup} ${styles.fullWidth}`}>
                    <label>Memo 2</label>
                    <TextArea
                        value={formData.memo2}
                        onChange={(e) => setFormData((prev) => ({ ...prev, memo2: (e.value ?? "").slice(0, 50) }))}
                        rows={1}
                        maxLength={50}
                        style={{ resize: "none" }}
                    />
                    <span style={{ fontSize: "0.75rem", color: "#666", textAlign: "right" }}>{(formData.memo2 ?? "").length}/50</span>
                </div>
            </div>

            <DialogActionsBar>
                <Button fillMode="outline" themeColor="base" onClick={onCancel}>
                    Cancel
                </Button>
                <Button
                    fillMode="solid"
                    themeColor="primary"
                    onClick={handleSave}
                    disabled={!isFormValid()}
                >
                    {showCpiFields ? (mode === "create" ? "Save CPI" : "Update CPI") : mode === "create" ? "Save Insurance" : "Update Insurance"}
                </Button>
            </DialogActionsBar>

            <InsuranceAgentSelector
                visible={showAgentSelector}
                agents={insuranceAgents}
                onSelect={handleAgentSelected}
                onCancel={() => setShowAgentSelector(false)}
            />
        </Dialog>
    );
}
